"use client";

import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog';
import { PlayCircle } from 'lucide-react';

type FloatingDot = {
  top: number;
  left: number;
  size: number;
  moveX: number;
  moveY: number;
  duration: number;
  delay: number;
};

const floatingDots: FloatingDot[] = [
  { top: 12, left: 18, size: 18, moveX: 22, moveY: -22, duration: 16, delay: 0 },
  { top: 28, left: 72, size: 24, moveX: -30, moveY: 30, duration: 18, delay: 0.8 },
  { top: 58, left: 24, size: 16, moveX: 24, moveY: -18, duration: 15, delay: 0.4 },
  { top: 70, left: 80, size: 20, moveX: 24, moveY: 24, duration: 20, delay: 1.6 },
  { top: 44, left: 48, size: 12, moveX: -26, moveY: 24, duration: 14, delay: 0.2 },
  { top: 20, left: 50, size: 12, moveX: 24, moveY: -26, duration: 12, delay: 1.1 },
  { top: 78, left: 30, size: 14, moveX: 20, moveY: -18, duration: 17, delay: 0.6 },
  { top: 38, left: 10, size: 10, moveX: 26, moveY: 20, duration: 13, delay: 0.9 },
];

const dotConnections = [
  { from: 0, to: 5, delay: 0 },
  { from: 0, to: 2, delay: 0.3 },
  { from: 2, to: 4, delay: 0.6 },
  { from: 4, to: 1, delay: 0.9 },
  { from: 1, to: 3, delay: 1.2 },
  { from: 3, to: 6, delay: 1.1 },
  { from: 6, to: 4, delay: 0.5 },
  { from: 7, to: 2, delay: 1.4 },
  { from: 7, to: 0, delay: 1.6 },
];

const buildKeyframes = (dot: FloatingDot) => {
  const pathX = [
    dot.left,
    dot.left + dot.moveX,
    dot.left - dot.moveX * 0.35,
    dot.left + dot.moveX * 0.25,
    dot.left,
  ];
  const pathY = [
    dot.top,
    dot.top + dot.moveY,
    dot.top - dot.moveY * 0.4,
    dot.top + dot.moveY * 0.2,
    dot.top,
  ];

  return { x: pathX, y: pathY };
};

const dotKeyframes = floatingDots.map(buildKeyframes);
const dotPercentKeyframes = dotKeyframes.map((frames) => ({
  top: frames.y.map((value) => `${value}%`),
  left: frames.x.map((value) => `${value}%`),
}));

const Hero = () => {
  return (
    <motion.section
      className="relative py-16 sm:py-20 md:py-32 overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="absolute inset-0 -z-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" aria-hidden="true" />
        <div className="absolute inset-0 opacity-80 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.4),_transparent_55%)]" aria-hidden="true" />
        <div className="absolute inset-0 opacity-60 blur-2xl bg-[radial-gradient(circle_at_bottom,_rgba(59,130,246,0.4),_transparent_60%)]" aria-hidden="true" />
        {floatingDots.map((dot, index) => {
          const frames = dotPercentKeyframes[index];
          return (
            <motion.span
              key={`dot-${index}`}
              className="absolute rounded-full bg-cyan-200/90 shadow-[0_0_30px_rgba(6,182,212,0.55)]"
              style={{ width: dot.size, height: dot.size }}
              initial={{ top: frames.top[0], left: frames.left[0], opacity: 0.7 }}
              animate={{
                top: frames.top,
                left: frames.left,
                opacity: [0.6, 1, 0.8, 1, 0.6],
                scale: [0.95, 1.15, 0.9, 1.2, 1],
              }}
              transition={{
                duration: dot.duration,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: dot.delay,
                times: [0, 0.25, 0.5, 0.75, 1],
              }}
              aria-hidden="true"
            />
          );
        })}
        <motion.svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="line-glow" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(8,145,178,0)" />
              <stop offset="40%" stopColor="rgba(6,182,212,0.85)" />
              <stop offset="60%" stopColor="rgba(14,165,233,0.9)" />
              <stop offset="100%" stopColor="rgba(6,182,212,0)" />
            </linearGradient>
          </defs>
          {dotConnections.map((connection, index) => {
            const { from, to, delay } = connection;
            const fromFrames = dotKeyframes[from];
            const toFrames = dotKeyframes[to];
            const duration = (floatingDots[from].duration + floatingDots[to].duration) / 2;
            return (
              <motion.line
                key={`connection-${index}`}
                stroke="url(#line-glow)"
                strokeWidth={0.6}
                strokeLinecap="round"
                initial={{
                  x1: fromFrames.x[0],
                  y1: fromFrames.y[0],
                  x2: toFrames.x[0],
                  y2: toFrames.y[0],
                  opacity: 0.5,
                }}
                animate={{
                  x1: fromFrames.x,
                  y1: fromFrames.y,
                  x2: toFrames.x,
                  y2: toFrames.y,
                  opacity: [0.4, 0.95, 0.7, 1, 0.45],
                }}
                transition={{
                  duration,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay,
                  times: [0, 0.25, 0.5, 0.75, 1],
                }}
              />
            );
          })}
        </motion.svg>
      </div>
      <div className="absolute inset-0 bg-black/40 -z-10" />

      <div className="relative z-10 container mx-auto px-4 md:px-6 text-center max-w-5xl">
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold text-white font-headline tracking-tight drop-shadow-md">
          Pioneering the Future of Decentralized Technology
        </h1>
        <p className="mt-5 sm:mt-6 max-w-3xl mx-auto text-base sm:text-lg md:text-xl text-gray-200 drop-shadow-sm">
          CryptoSI is at the forefront of blockchain innovation. We build robust solutions, educate communities, and connect businesses with elite developer talent in the crypto space.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-2xl mx-auto">
          <Link href="#projects" passHref>
            <Button size="lg" className="font-semibold w-full sm:w-auto">
              Explore Our Work
            </Button>
          </Link>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                size="lg"
                variant="outline"
                className="font-semibold border-white text-white bg-white/10 hover:bg-white/20 hover:text-white focus-visible:ring-white w-full sm:w-auto"
              >
                <PlayCircle className="mr-2 h-5 w-5" />
                Watch Intro
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-[90vw] sm:max-w-4xl p-0 bg-black border-0 rounded-lg overflow-hidden">
                <DialogTitle className="sr-only">Introduction to CryptoSI Video</DialogTitle>
                <div className="aspect-video">
                    <iframe
                        src="https://www.youtube.com/embed/21x4ofbNjMA?autoplay=1"
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                    ></iframe>
                </div>
            </DialogContent>
          </Dialog>
          <Link href="#services" passHref>
            <Button size="lg" variant="secondary" className="font-semibold w-full sm:w-auto">
              Our Services
            </Button>
          </Link>
        </div>
      </div>

      <motion.div
        className="absolute bottom-10 right-10 z-20 hidden md:flex items-center gap-5 p-6 rounded-2xl bg-card/20 backdrop-blur-md border border-white/20 shadow-xl animate-pulse-glow"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <Image src="/icontrans.png" alt="CryptoSI Icon" width={60} height={60} className="h-16 w-16" />
        <div className="text-left">
          <span className="text-base font-semibold text-white/80 block leading-tight">Established</span>
          <span className="text-4xl font-bold text-white block leading-tight">2012</span>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default Hero;
